"use client";

import { useState } from "react";
import Link from "next/link";
import { Menu, X, MessageCircle } from "lucide-react";
import Image from "next/image";
import ItineraryCreationModal from "./ItineraryCreationModal";
import NavLinks from "./NavLinks";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/blog", label: "Blog" },
    { href: "/trending", label: "Trending" },
    { href: "/itineraries", label: "Itineraries" },
    { href: "/my-trips", label: "My Trips" },
  ];

  // Chat link is extracted to give it special treatment
  const chatLink = { href: "/chat", label: "Chat" };

  const headerClasses =
    "fixed top-0 left-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50";

  return (
    <>
      <div className="h-16"></div>
      <header className={headerClasses}>
        <div className="container mx-auto px-4 py-3 flex justify-between items-center max-w-6xl">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center space-x-3 group"
            aria-label="AI Trip Maker Home"
          >
            <Image
              src="/icons/icon.svg"
              alt="AI Trip Maker Logo"
              width={40}
              height={40}
              className="w-10 h-10 group-hover:scale-110 transition-transform"
            />
            <span className="text-2xl font-bold text-gray-800 group-hover:text-gray-600 transition-colors">
              AI Trip Maker
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 ml-auto">
            <NavLinks
              links={navLinks}
              className="text-gray-600 hover:text-orange-600 font-medium transition-colors"
            />

            {/* Special styling for Chat link in desktop */}
            <Link
              href={chatLink.href}
              className="flex items-center gap-1 text-orange-600 font-semibold hover:text-orange-800 transition-colors px-3 py-1 rounded-lg border-2 border-orange-400 hover:border-orange-600 bg-orange-50"
              aria-label="Open AI Chat"
            >
              <MessageCircle size={20} />
              <span>{chatLink.label}</span>
            </Link>

            <button
              onClick={() => setModalOpen(true)}
              className="ml-4 px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-400 text-white rounded-full hover:from-orange-600 hover:to-amber-500 transition-colors font-semibold shadow-md hover:shadow-lg cursor-pointer"
            >
              Plan Trip
            </button>
          </nav>

          {/* Mobile Menu Toggle + Chat Shortcut */}
          <div className="md:hidden flex items-center gap-2">
            {/* Chat button always visible on mobile */}
            <Link
              href={chatLink.href}
              className="flex items-center gap-1 text-orange-600 bg-orange-50 py-2 px-3 rounded-lg border border-orange-300"
              aria-label="Open AI Chat"
              onClick={() => setMenuOpen(false)}
            >
              <MessageCircle size={20} />
              <span className="font-medium">Chat</span>
            </Link>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-gray-700 p-2 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label={menuOpen ? "Close Menu" : "Open Menu"}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {menuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-md py-4 px-4 space-y-3">
            <NavLinks
              links={navLinks}
              className="block text-center text-gray-700 hover:text-orange-600 py-2 rounded-md hover:bg-gray-100 transition-colors"
              onClick={() => setMenuOpen(false)}
            />
            <button
              onClick={() => {
                setMenuOpen(false);
                setModalOpen(true);
              }}
              className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-400 text-white rounded-full hover:from-orange-600 hover:to-amber-500 transition-colors font-semibold cursor-pointer"
            >
              Plan Trip
            </button>
          </div>
        )}
      </header>

      {/* Modal for Itinerary Creation */}
      <ItineraryCreationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
